import { AlertTriangle, HeartCrack } from "lucide-react";
import { useNavigate } from "react-router";

import { EmptyState } from "@/components/ui/empty-state";
import { PUBLIC_ROUTES } from "@/constants/routes";
import { useFavoriteToggleHandler } from "@/features/engagement/hooks/useFavoriteToggleHandler";
import { useWishlist } from "@/features/engagement/hooks/useWishlist";
import { ProductCard, ProductCardSkeleton } from "@/features/marketplace/components/ProductCard";
import { ApiError } from "@/types/api";

const SKELETON_COUNT = 6;

/**
 * docs/architecture/claude/ui-architecture.md §"Empty states" #2: heart-
 * broken icon, "Your wishlist is empty" / "Save items you're interested
 * in...", an "Explore Products" CTA — used verbatim here. Every wishlisted
 * item is, by definition, favorited, so `favoriteIds` is just every id
 * currently on the page — no separate membership check needed the way
 * Home/Search need one.
 */
export function WishlistPage() {
  const navigate = useNavigate();
  const wishlistQuery = useWishlist();
  const favoriteIds = new Set(wishlistQuery.data?.map((product) => product.id) ?? []);
  const handleFavoriteToggle = useFavoriteToggleHandler(favoriteIds);

  return (
    <div className="mx-auto max-w-[1280px] px-4 py-8">
      <h1 className="mb-6 text-h1 text-foreground">Wishlist</h1>

      {wishlistQuery.isPending && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {Array.from({ length: SKELETON_COUNT }, (_, index) => (
            <ProductCardSkeleton key={index} />
          ))}
        </div>
      )}

      {wishlistQuery.isError && (
        <EmptyState
          icon={AlertTriangle}
          heading="Couldn't load your wishlist"
          description={
            wishlistQuery.error instanceof ApiError
              ? wishlistQuery.error.message
              : "Something went wrong. Please try again."
          }
          action={{ label: "Retry", onClick: () => void wishlistQuery.refetch() }}
        />
      )}

      {wishlistQuery.data?.length === 0 && (
        <EmptyState
          icon={HeartCrack}
          heading="Your wishlist is empty"
          description="Save items you're interested in so you can find them again later."
          action={{ label: "Explore Products", onClick: () => navigate(PUBLIC_ROUTES.home) }}
        />
      )}

      {wishlistQuery.data && wishlistQuery.data.length > 0 && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {wishlistQuery.data.map((product) => (
            <ProductCard
              key={product.id}
              product={product}
              isFavorite
              onFavoriteToggle={handleFavoriteToggle}
            />
          ))}
        </div>
      )}
    </div>
  );
}
